<template>
  <div>
    <custom-callout
      apply-border-top
      :border-color="statusColor"
      class-main="mt-3 rounded"
      controlled
      has-border
      has-gradient
      has-toggle
      :opened="data.isOpen"
      @click:open="(value) => handleToggle(value, actions.VERSION_HISTORY)"
    >
      <div class="row mr-md-0">
        <div
          class="col-12 col-md-2 d-flex flex-column align-items-start align-items-md-center justify-content-start justify-content-md-center pt-2"
        >
          <div class="d-flex align-items-center justify-content-center">
            <em
              class="fa fa-circle font-12 mr-2"
              :class="`color-${statusColor}`"
            />
            <span class="font-16 font-weight-bold">
              {{ data.statusName | empty }}
            </span>
          </div>
          <div
            class="d-flex flex-column align-items-start align-items-md-center justify-content-start justify-content-md-center mt-3"
          >
            <span class="data-title d-block border-mobile">Año Académico:</span>
            <span class="font-16 color-black mt-n1">
              {{ data.academicYearName | empty }}
            </span>
          </div>
        </div>
        <div
          class="col-md-8 align-items-center pl-3 pl-md-4"
          :class="!isMobile ? 'border-left' : ''"
        >
          <div class="row">
            <div class="col-12 col-md-3 my-2">
              <span class="data-title d-block border-mobile">
                Tipo de Contrato:
              </span>
              <span class="font-16 color-black">
                {{ data.contractTypeName }}
              </span>
            </div>
            <div class="col-12 col-md-3 my-2">
              <span class="data-title d-block border-mobile">
                Creador Por:
              </span>
              <span class="font-16 color-black">
                {{ data.createdByName }}
              </span>
            </div>
            <div class="col-12 col-md-3 my-2">
              <span class="data-title d-block">Fecha de Creación:</span>
              <span class="font-16 color-black">
                {{ data.createdDate | date('DD/MM/YYYY') }}
              </span>
            </div>
            <div class="col-12 col-md-3 my-2">
              <span class="data-title d-block">Última Modificación:</span>
              <span class="font-16 color-black">
                {{ data.updatedDate | date('DD/MM/YYYY') }}
              </span>
            </div>
          </div>
        </div>
        <div
          class="col col-sm col-12 d-flex align-items-center justify-content-center"
        >
          <drop-down
            class="mt-2 actions-dropdown"
            :items="actionItems(data)"
            title="Acciones"
          />
        </div>
      </div>
      <template #edition>
        <div class="p-2">
          <div class="bg-white rounded p-md-3 px-md-4">
            <contract-versions
              v-if="isVisibleVersionHistory"
              :items="data.versions"
              @on-view-version="$emit('on-view-version', $event)"
            />
          </div>
        </div>
      </template>
    </custom-callout>
  </div>
</template>

<script>
// libs
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';

// child components
import ContractVersions from './ContractVersions.vue';

// store
import store, { modulesName } from '@/store/index';

// constants
import rpContractEditorVersionStatus, {
  rpContractEditorVersionStatusColor,
} from '@/utils/constants/rpContractEditorVersionStatus.js';
const ACTIONS = {
  VERSION_HISTORY: 'VERSION_HISTORY',
};

export default {
  name: 'ResultRow',
  components: {
    CustomCallout,
    DropDown,
    ContractVersions,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    actions() {
      return ACTIONS;
    },
    isVisibleVersionHistory() {
      return this.action === ACTIONS.VERSION_HISTORY;
    },
    isMobile() {
      return store.getters[`${modulesName.appModuleName}/getIsMobileDensity`];
    },
    actionItems() {
      return () => [
        {
          label: 'Editar',
          icon: 'fas fa-edit color-green',
          visible: !this.isPublished,
          click: () => this.$emit('on-edit', { ...this.data }),
        },
        {
          label: 'Historial de Versiones',
          icon: 'fas fa-history color-green',
          visible: this.canShowHistory,
          click: () => this.handleToggle(true, ACTIONS.VERSION_HISTORY),
        },
        {
          label: 'Crear Nueva Versión',
          icon: 'fas fa-plus-circle color-green',
          border: this.isPublished ? 'top' : '',
          visible: this.isPublished,
          click: () => this.$emit('on-create-new-version', { ...this.data }),
        },
        {
          label: 'Descartar Borrador',
          icon: 'fas fa-times-circle color-red mt-2',
          border: 'top',
          visible: !this.isPublished && this.canDiscardDraft,
          click: () => this.$emit('on-discard-draft', { ...this.data }),
        },
      ];
    },
    statusColor() {
      return (
        {
          [rpContractEditorVersionStatus.Draft]:
            rpContractEditorVersionStatusColor[
              rpContractEditorVersionStatus.Draft
            ],
          [rpContractEditorVersionStatus.Published]:
            rpContractEditorVersionStatusColor[
              rpContractEditorVersionStatus.Published
            ],
          [rpContractEditorVersionStatus.Inactive]:
            rpContractEditorVersionStatusColor[
              rpContractEditorVersionStatus.Inactive
            ],
        }[this.data.statusId] || null
      );
    },
    isPublished() {
      return this.data.statusId === rpContractEditorVersionStatus.Published;
    },
    canDiscardDraft() {
      return this.data.versions.filter(
        (x) => x.statusId === rpContractEditorVersionStatus.Published
      )?.length;
    },
  },
  watch: {
    data: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          if (newVal.isOpen && newVal.action) {
            this.action = newVal.action;
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async handleToggle(opened = !this.data.isOpen, action) {
      this.action = action;
      this.data.isOpen = opened;
      this.data.action = action;
      this.$emit('toggle', this.data);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';
</style>
